import type {
  ObsoletePropertiesHyphen,
  StandardLonghandPropertiesHyphen,
  VendorPropertiesHyphen,
} from 'csstype';
import type { StandardShorthandPropertiesHyphen } from 'csstype';
import { css } from 'styled-components';

import type { Maybe } from '@/lib/types';

import { media } from './media';
import type { BreakpointsKeys } from './types';

type Props = ObsoletePropertiesHyphen &
  StandardLonghandPropertiesHyphen &
  StandardShorthandPropertiesHyphen &
  VendorPropertiesHyphen;

export const responsive = <Prop extends keyof Props>(
  prop: Prop,
  mobileValue: Props[Prop],
  desktopValue: Props[Prop],
  breakpoint: BreakpointsKeys = 'md',
) => `
  ${prop}: ${mobileValue};
  ${
    breakpoint &&
    `
    ${media[breakpoint].up} {
        ${prop}: ${desktopValue};
      }
    `
  }
`;

export const conditionalStyle = <Prop extends keyof Props>(
  shouldApply: Maybe<boolean>,
  prop: Prop,
  value: Props[Prop],
) =>
  !!shouldApply &&
  css`
    ${prop}: ${value};
  `;

export const horizontalCenter = `
  content: "";
  position: absolute;
  left: 50%;
  transform: translate(-50%);
`;

export const verticalCenter = `
  content: "";
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`;

export const absoluteCenter = `
  content: "";
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

export const hiddenUp = (breakpoint: BreakpointsKeys) => `
  ${media[breakpoint].up} {
    display: none;
  }
`;

export const hiddenDown = (breakpoint: BreakpointsKeys) => `
  ${media[breakpoint].down} {
    display: none;
  }
`;

export const hiddenOnly = (breakpoint: BreakpointsKeys) => `
  ${media[breakpoint].only} {
    display: none;
  }
`;

export const textUnderlineOnHover = css<{
  $isActive?: boolean;
  $underline?: boolean;
}>`
  position: relative;

  & *:is(span, p) {
    position: relative;

    /* The default underline style */
    &::before {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      height: ${({ $isActive, $underline = true }) =>
        $underline ? '1px' : $isActive ? '1.5px' : 0};
      width: ${({ $isActive, $underline = true }) =>
        $isActive || $underline ? '100%' : 0};
      background-color: ${({ $isActive, theme }) =>
        $isActive ? 'currentColor' : theme.colors.primary};
    }

    /* The underline style on hover */
    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 0;
      transition: width 0.3s ease;
    }
  }

  /* When hovering the whole button, add underline to text */
  &:hover :is(span, p)::after {
    width: 100%;
    height: 1.5px;
    background-color: currentColor;
  }
`;

export const removeTextUnderlineOnHover = css`
  cursor: pointer;
  text-decoration: none;

  & *:is(span, p) {
    position: relative;

    /* Text underline */
    &::after {
      content: '';
      position: absolute;
      right: 0;
      bottom: 0;
      height: 1px;
      width: 100%;
      background-color: currentColor;
      transition: width 0.3s ease;
    }
  }

  /* Remove the underline when hovering */
  &:hover :is(span, p)::after {
    width: 0;
  }
`;
